import { useEffect, useRef, useState } from 'react'
import type { CourseWithoutBlocks } from '@/types/course'
import type { Lang } from '@/types/lang'
import {
  hasServerTranslationRow,
  hasTranslationRow,
  isTranslationPublished,
  translationLangOf,
  unpublishedFilledLang,
} from '@/features/editor/lib/courseTranslation'
import { useCourseLanguages } from '@/features/ai/useCourseLanguages'
import { useIsDraftSourceSettling } from '@/features/moderation/lib/draftSourceSettling'
import { useAppSelector } from '@/store/hooks'
import { resolveLang } from '../studio/seedContentLang'
import { useHasStartedTranslationRun, useIsOwnRunActive } from '../studio/useBlocksInRun'
import { DeleteLanguageDialog } from '../EditorHeader/DeleteLanguageDialog'
import { useLanguageActions } from '../EditorHeader/useLanguageActions'
import { useGetEditorCourseQuery } from '@/features/editor/coursesApi'
import { AddLanguageMenu } from './AddLanguageMenu'
import { BaseLangHint } from './BaseLangHint'
import { LanguageCard } from './LanguageCard'
import { TranslationLangRow } from './TranslationLangRow'
import { useCourseRereadQueued } from './useCourseRereadQueued'
import { useMessages } from '@/i18n'
import { messages } from './messages'

interface Props {
  course: CourseWithoutBlocks
}

/** Вкладка «Язык» (спека §02): базовый язык, список языков курса со статусами,
 *  добавление и удаление. Запуска перевода здесь нет — он только в меню языка
 *  шапки, оба места читают одни данные (`useCourseLanguages`). */
export const LanguagesSection = ({ course }: Props) => {
  const m = useMessages(messages)
  const current = useAppSelector((s) => s.editor.contentLang)
  const startedRun = useHasStartedTranslationRun(course.id)
  const isRunActive = useIsOwnRunActive(course.id)
  const languages = useCourseLanguages(course.id)
  // Спека §08: статусы свежие в момент открытия вкладки. Смета могла лечь в
  // кеш давно (меню шапки), а тег `Quote` протухает только на конце прогона.
  // Через ref — `refetch` новый на каждом рендере, а перечитать нужно ровно
  // раз на курс.
  const refetchLanguages = useRef(languages.refetch)
  refetchLanguages.current = languages.refetch
  useEffect(() => {
    refetchLanguages.current()
  }, [course.id])
  const actions = useLanguageActions(course)
  // После смены базового статусы и тумблеры пересчитывает сервер: пока курс и
  // смета не перечитаны, показываем скелетоны, а не старые значения.
  const { isFetching: courseFetching } = useGetEditorCourseQuery(course.id)
  const rereadQueued = useCourseRereadQueued(course.id, actions.writtenAt)
  // Первая запись в живой курс заводит черновик: чтение курса сначала отдаёт живую версию без
  // этой записи и только после переезда на черновик — настоящую.
  const sourceSettling = useIsDraftSourceSettling(course.id)
  // Экран меняется сразу, а следующее действие — только когда сервер ответил и курс перечитан:
  // поверх неподтверждённого сервер отвечал отказом, а подсказки мелькали ложно.
  const waitingServer = actions.isBusy || courseFetching || rereadQueued || sourceSettling
  const settling = waitingServer || languages.isFetching
  const [rebaseTo, setRebaseTo] = useState<Lang | null>(null)
  useEffect(() => {
    if (rebaseTo && !settling) setRebaseTo(null)
  }, [rebaseTo, settling])
  const [deleting, setDeleting] = useState<Lang | null>(null)
  // Выбор автора держим на экране, пока сервер не ответил окончательно: промежуточное чтение
  // не должно откатывать тумблер. Отказ снимает выбор, и экран возвращается к данным курса.
  const [wanted, setWanted] = useState<boolean | null>(null)
  useEffect(() => {
    if (wanted !== null && !waitingServer) setWanted(null)
  }, [wanted, waitingServer])
  const source = rebaseTo ?? resolveLang(course.sourceLang)
  const target = rebaseTo ? translationLangOf({ sourceLang: rebaseTo }) : translationLangOf(course)
  const added =
    wanted ?? (rebaseTo !== null || hasTranslationRow(course, startedRun) || current === target)
  // Добавлять язык можно и во время прогона: объявление языка сервер принимает (§6.12).
  const addDisabled = waitingServer || actions.isFrozen
  const disabled = isRunActive || addDisabled
  const canDeleteTarget = hasServerTranslationRow(course, target, startedRun)
  return (
    <div className="space-y-4">
      <LanguageCard lang={source} isBase />
      {added ? (
        // Пока язык добавляется, видимости нет: менять её ещё нечему.
        wanted === null ? (
          <TranslationLangRow
            hideBadge
            loading={rebaseTo !== null}
            courseId={course.id}
            lang={target}
            state={languages.stateOf(target)}
            // Прежний базовый виден ученикам, а строки перевода в кеше у него ещё нет: без этого тумблер мигал «выкл».
            isPublished={rebaseTo !== null || isTranslationPublished(course, target)}
            // Пока курс перечитывается, строки языка и текст расходятся: подсказка мелькала бы ложно.
            isLangUndeclared={Boolean(
              !settling && unpublishedFilledLang(course, startedRun) === target,
            )}
            locked={waitingServer}
            isCourseDraft={!course.isPublished}
            layout={({ status, control, notes }) => (
              <LanguageCard
                lang={target}
                status={status}
                control={control}
                disabled={disabled}
                disabledHint={isRunActive ? m.actionsDuringRun : null}
                makeBaseBlocked={
                  rebaseTo === null ? !isTranslationPublished(course, target) : false
                }
                onDelete={canDeleteTarget ? () => setDeleting(target) : undefined}
                onMakeBase={() => {
                  setRebaseTo(target)
                  void actions.makeBase(target)
                }}
              >
                {notes}
              </LanguageCard>
            )}
          />
        ) : (
          <LanguageCard lang={target} disabled pending />
        )
      ) : (
        <AddLanguageMenu
          langs={[target]}
          disabled={addDisabled}
          pending={wanted !== null}
          onAdd={(lang) => {
            setWanted(true)
            void actions.add(lang)
          }}
        />
      )}
      <BaseLangHint />
      <DeleteLanguageDialog
        lang={deleting}
        isDeleting={actions.isBusy}
        onOpenChange={(open) => !open && setDeleting(null)}
        onConfirm={() => {
          const lang = deleting
          setDeleting(null)
          if (!lang) return
          setWanted(false)
          void actions.remove(lang)
        }}
      />
    </div>
  )
}
